import { useState, useEffect, useCallback } from 'react';
import {
  ReferralStats,
  Referral,
  generateReferralCode,
  getReferralStats,
  getReferralHistory,
  getReferralLink
} from '../services/referral';

interface UseReferralReturn {
  stats: ReferralStats | null;
  history: Referral[];
  loading: boolean;
  error: string | null;
  referralLink: string | null;
  refresh: () => Promise<void>;
  copyLink: () => Promise<boolean>;
}

export function useReferral(): UseReferralReturn {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [history, setHistory] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await generateReferralCode();

      const [statsResult, historyResult] = await Promise.all([
        getReferralStats(),
        getReferralHistory()
      ]);

      if (statsResult) {
        setStats(statsResult);
      }
      setHistory(historyResult);
    } catch (err) {
      setError('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const referralLink = stats?.code ? getReferralLink(stats.code) : null;

  const copyLink = useCallback(async (): Promise<boolean> => {
    if (!referralLink) return false;

    try {
      await navigator.clipboard.writeText(referralLink);
      return true;
    } catch {
      return false;
    }
  }, [referralLink]);

  return {
    stats,
    history,
    loading,
    error,
    referralLink,
    refresh: fetchData,
    copyLink
  };
}
